import React, { ReactNode } from "react";
import { TableCell } from "@mui/material";
import { toast } from "react-toastify";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal/Modal";
import { CommonTableUI } from "../../TableTemplate/common";
import { IAdditional, IAdditionalHeaderProps, ITableUI } from "../../TableTemplate/tableTemplate";
import { Packet } from "../../../Interfaces/commonDtos";
import {
  IFailedOrderBulkPreviewResponse,
  IFailedOrderResendResult,
  IFailedOrderTableReport,
} from "../../../Interfaces/orderInterfaces";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import PreviewVolumeSummaryTable from "../PreviewVolumeSummaryTable";

export interface IFailedOrderResendPreview {
  rows: any[];
  orderDto: any;
  volumeSummary?: any[];
}

interface IProps extends ITableUI<IFailedOrderTableReport> {
  previewResend: (id: number) => Promise<IFailedOrderResendPreview>;
  resendOrder?: (id: number, orderDto: any) => Promise<Packet<boolean>>;
  sendOrder?: (orderDto: any) => Promise<Packet<boolean>>;
  markResolved?: (id: number) => Promise<Packet<boolean>>;
  renderPreviewRows: (rows: any[]) => ReactNode;
  previewResendBulk: (
    ids: number[],
  ) => Promise<IFailedOrderBulkPreviewResponse<IFailedOrderResendPreview>>;
  resendBulk: (
    items: Array<{ id: number; orderDto: any }>,
  ) => Promise<IFailedOrderResendResult[]>;
}

const getExecutableOrderDto = (preview: IFailedOrderResendPreview) => {
  const executableRows = (preview.rows || []).filter((row: any) => row.totalVolume > 0);
  if (!executableRows.length) return null;

  const executableUserIds = executableRows.map((row: any) => row.userId);
  const executableUserIdSet = new Set(executableUserIds);
  const executableVolumePerUser = Object.fromEntries(
    Object.entries(preview.orderDto?.volumePerUser || {}).filter(([key]) =>
      executableUserIdSet.has(Number(key)),
    ),
  );

  return {
    ...preview.orderDto,
    userIds: executableUserIds,
    volumePerUser: executableVolumePerUser,
  };
};

interface IBulkPreviewItem {
  id: number;
  row: IFailedOrderTableReport;
  preview: IFailedOrderResendPreview;
}

const FailedOrdersTableUI: React.FC<IProps> = ({
  reports,
  onAction,
  previewResend,
  resendOrder,
  sendOrder,
  markResolved,
  renderPreviewRows,
  previewResendBulk,
  resendBulk,
  tableBoxClassName,
  refreshFlag,
}) => {
  const FailedOrdersTableAdditionalHeader: React.FC<IAdditionalHeaderProps> = ({
    selectedRowsIds,
    setSelectedRowsIds,
    onAction: headerOnAction,
  }) => {
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [bulkItems, setBulkItems] = React.useState<IBulkPreviewItem[] | null>(null);
    const [bulkVolumeSummary, setBulkVolumeSummary] = React.useState<any[]>([]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const openBulkPreview = () => {
      if (!selectedRowsIds.length) return;
      setPreviewLoading(true);
      previewResendBulk(selectedRowsIds)
        .then((response) => {
          const succeeded: IBulkPreviewItem[] = [];
          let failedCount = 0;
          response.items.forEach((item) => {
            const row = item.success && item.preview
              ? reports.report.find((r) => r.id === item.id)
              : undefined;
            if (row && item.preview) succeeded.push({ id: item.id, row, preview: item.preview });
            else failedCount++;
          });
          if (failedCount) toast.error(`پیش‌نمایش ${failedCount} مورد با خطا مواجه شد.`);
          if (!succeeded.length) return;
          setBulkItems(succeeded);
          setBulkVolumeSummary(response.volumeSummary || []);
          setIsModalOpen(true);
        })
        .catch(packetErrorHandler)
        .finally(() => setPreviewLoading(false));
    };

    const closeModal = () => {
      setIsModalOpen(false);
      setBulkItems(null);
      setBulkVolumeSummary([]);
    };

    const sendAllHandler = () => {
      if (!bulkItems || !bulkItems.length) return;

      const sendItems = bulkItems
        .map((item) => ({ id: item.id, orderDto: getExecutableOrderDto(item.preview) }))
        .filter((item): item is { id: number; orderDto: any } => !!item.orderDto);
      const skippedCount = bulkItems.length - sendItems.length;

      if (!sendItems.length) {
        toast.error("مشکل هنوز برطرف نشده است.");
        return;
      }

      setSending(true);
      resendBulk(sendItems)
        .then((results) => {
          const succeededCount = results.filter((r) => r.success).length;
          const failedCount = results.length - succeededCount + skippedCount;
          if (succeededCount) toast.success(`${succeededCount} سفارش با موفقیت ارسال شد.`);
          if (failedCount) toast.error(`${failedCount} سفارش ارسال نشد.`);
          closeModal();
          setSelectedRowsIds([]);
          headerOnAction();
        })
        .catch(packetErrorHandler)
        .finally(() => setSending(false));
    };

    return (
      <TableCell>
        {isModalOpen ? (
          <Modal
            onClose={closeModal}
            fullScreen
            UI={
              <div className="flex flex-col justify-start items-stretch gap-5 p-3 overflow-auto h-full">
                <div className="w-fit max-w-full mx-auto">
                  <PreviewVolumeSummaryTable values={bulkVolumeSummary} />
                </div>
                {renderPreviewRows((bulkItems || []).flatMap((item) => item.preview.rows || []))}
                <Button
                  onClick={sendAllHandler}
                  text="ارسال همه"
                  variant="contained"
                  className="green-btn w-full"
                  loading={sending}
                />
              </div>
            }
          />
        ) : null}
        <Button
          onClick={openBulkPreview}
          className="table-btn"
          text="ارسال مجدد گروهی"
          variant="contained"
          loading={previewLoading}
          disabled={!selectedRowsIds.length}
        />
      </TableCell>
    );
  };

  const FailedOrdersTableAdditionalRow: React.FC<{
    row: IFailedOrderTableReport;
    onAction: () => void;
  }> = ({ row, onAction }) => {
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [preview, setPreview] = React.useState<IFailedOrderResendPreview | null>(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const openPreview = () => {
      setPreviewLoading(true);
      previewResend(row.id)
        .then((resp) => {
          setPreview(resp);
          setIsModalOpen(true);
        })
        .catch(packetErrorHandler)
        .finally(() => setPreviewLoading(false));
    };

    const closeModal = () => {
      setIsModalOpen(false);
      setPreview(null);
    };

    const sendHandler = () => {
      if (!preview) return;
      const orderDto = getExecutableOrderDto(preview);
      if (!orderDto) {
        toast.error("مشکل هنوز برطرف نشده است.");
        return;
      }

      const request = resendOrder
        ? resendOrder(row.id, orderDto)
        : sendOrder?.(orderDto);
      if (!request) return;

      setSending(true);
      request
        .then((resp) => {
          if (!resp.data) {
            toast.error(resp.msg);
            return;
          }
          toast.success(resp.msg);
          if (resendOrder) {
            closeModal();
            onAction();
            return;
          }
          if (!markResolved) return;
          markResolved(row.id)
            .catch(packetErrorHandler)
            .finally(() => {
              closeModal();
              onAction();
            });
        })
        .catch(packetErrorHandler)
        .finally(() => setSending(false));
    };

    return (
      <TableCell>
        {isModalOpen ? (
          <Modal
            onClose={closeModal}
            UI={
              <div className="flex flex-col justify-start items-center gap-5 p-3">
                <PreviewVolumeSummaryTable values={preview?.volumeSummary} />
                {renderPreviewRows(preview?.rows || [])}
                <div className="w-40">
                  <Button
                    onClick={sendHandler}
                    text="ارسال"
                    variant="contained"
                    className="green-btn"
                    loading={sending}
                  />
                </div>
              </div>
            }
          />
        ) : null}
        <Button
          onClick={openPreview}
          className="table-btn"
          text="ارسال مجدد"
          variant="outlined"
          loading={previewLoading}
          disabled={row.isResentSuccessfully}
        />
      </TableCell>
    );
  };

  const AdditionalBody: IAdditional<IFailedOrderTableReport> = {
    getHeader: FailedOrdersTableAdditionalHeader,
    getRow: FailedOrdersTableAdditionalRow,
  };

  return (
    <CommonTableUI
      reports={reports}
      onAction={onAction}
      AdditionalBody={AdditionalBody}
      withSelect
      isRowSelectable={(row) => !row.isResentSuccessfully}
      tableBoxClassName={`max-h-[500px] overflow-auto ${tableBoxClassName ?? ""}`}
      refreshFlag={refreshFlag}
    />
  );
};

export default FailedOrdersTableUI;
